import {
    CancelRefundFooterProps,
    CancelRefundHeaderProps,
    OrderRefundDetails,
    SelectedForRefund,
    TicketsForRefund,
} from '../types'
import { User } from 'auth/state'
import { RefundPaypalDetails } from 'orders/paypalDisputes/schema'
import { DateFormats, formatISOString } from 'utils/dates'
import {
    CancelRefundTableData,
    CancelRefundTableSection,
    ICancelRefundTableRow,
} from '../components/cancelRefundTable/types'
import { RefundFeeType } from 'settings/accountSettings/contract/managementService'
import { BookingFeeRowData, getBookingFeesRowsData } from '../utils'
import { t } from 'translations'

interface headerPropsMapperOptions {
    accountSlug: string
    user: User | null
    isCancellation: boolean
    isPostCancellationRefund?: boolean
    formatCurrencyString: (amount: number | string, accountSlug: string) => string
    order?: OrderRefundDetails
    paypalRefund?: RefundPaypalDetails
    onClose: () => void
}

export const mapHeaderData = ({
    accountSlug,
    user,
    isCancellation,
    isPostCancellationRefund = false,
    formatCurrencyString,
    order,
    paypalRefund,
    onClose,
}: headerPropsMapperOptions): CancelRefundHeaderProps => {
    return {
        title: isCancellation
            ? t('orders.orders.cancelAndRefund.single.cancelItems')
            : isPostCancellationRefund
            ? t('orders.orders.cancelAndRefund.single.refundCancelledOrder')
            : t('orders.orders.cancelAndRefund.single.refundItems'),
        orderNumber: order?.number ?? 'XXXXXXXX',
        isAdminUser: user?.isAdmin ?? false,
        customerEmail: order?.customer?.email ?? '',
        visitDate: order?.visitDate ? formatISOString(order.visitDate, DateFormats.EXTRA_LONG_DATE) : undefined,
        paypalRefundData: paypalRefund
            ? {
                  id: paypalRefund.id,
                  accountName: paypalRefund.accountName,
                  location: paypalRefund.location,
                  reason: paypalRefund.reason,
                  refundAmount: formatCurrencyString(paypalRefund.refundAmount.toFixed(2), accountSlug),
              }
            : undefined,
        onClose,
    }
}

interface TableDataMapperOptions {
    tickets: TicketsForRefund[]
    selectedTickets: SelectedForRefund[]
    totalAmount: number
    partnerRefundFeeAmount: number
    partnerRefundFeeOff: boolean
    partnerRefundFeeSelected: boolean
    showPartialRedeemWarning: boolean
    partnerRefundFee?: string
    partnerRefundFeeType?: RefundFeeType | null
    bookingFeesEnabled: boolean
    confirmMode: boolean
    isCancellation: boolean
}

export const prepareTableData = ({
    tickets,
    selectedTickets,
    totalAmount,
    partnerRefundFeeAmount,
    partnerRefundFeeOff,
    partnerRefundFeeSelected,
    showPartialRedeemWarning,
    partnerRefundFee,
    partnerRefundFeeType,
    bookingFeesEnabled,
    confirmMode,
    isCancellation,
}: TableDataMapperOptions): CancelRefundTableData => {
    const sections: CancelRefundTableSection[] = []
    const ticketRows: ICancelRefundTableRow[] = []
    const bookingFeeRowsData: BookingFeeRowData[] = []

    for (const ticket of tickets) {
        for (const item of ticket.items) {
            const ticketEntity = item.ticketEntity
            const barcodeString = item.barcodeList.join(', ') || '-'
            const isSelected = selectedTickets.some((selected) => selected.ticketUuids.includes(ticketEntity.ids[0]))

            if (!isSelected && confirmMode) {
                continue
            }

            ticketRows.push({
                id: ticketEntity.ids[0],
                rowType: 'ticket',
                name: ticket.product,
                description:
                    item.barcodeList.length > 1
                        ? t('orders.orders.cancelAndRefund.single.hoverBarcodes')
                        : barcodeString,
                descriptionInfo: barcodeString,
                amount: Number(item.price),
                selected: isSelected,
                orderItemId: ticket.orderItemId,
                disabled: !item.itemRefundable,
            })

            getBookingFeesRowsData(
                bookingFeeRowsData,
                ticketEntity,
                isSelected,
                ticket.product,
                ticket.orderItemId,
                ticket.isBundle,
            )
        }
    }

    const ticketsSection = {
        id: 'itemsSection',
        rows: ticketRows,
        borderBottom: true,
    }

    sections.push(ticketsSection)

    if (bookingFeesEnabled && bookingFeeRowsData.length > 0) {
        const bookingFeeRows: ICancelRefundTableRow[] = []
        for (const bookingFee of bookingFeeRowsData) {
            const isBookingFeeSelected = selectedTickets
                .flatMap((ticket) => ticket.bookingFeeUuids)
                .includes(bookingFee.id)

            if (!isBookingFeeSelected && confirmMode) {
                continue
            }

            bookingFeeRows.push({
                id: bookingFee.id,
                rowType: 'bookingFee',
                name: bookingFee.name,
                description: t('orders.orders.cancelAndRefund.productsSelected', {
                    selected: bookingFee.amountOfSelectedProducts,
                    total: bookingFee.amountOfProducts,
                }),
                amount: bookingFee.amount,
                selected: isBookingFeeSelected,
                orderItemId: bookingFee.orderItemId,
                infoTip: `* ${bookingFee.productName}`,
                disabled: bookingFee.amountOfSelectedProducts === 0,
            })
        }

        const bookingFeeSection = {
            id: 'bookingFeeSection',
            rows: bookingFeeRows,
            borderBottom: true,
        }

        sections.push(bookingFeeSection)
    }

    if (!partnerRefundFeeOff && !isCancellation) {
        const refundFeeSection = {
            id: 'refundFeeSection',
            rows:
                confirmMode && !partnerRefundFeeSelected
                    ? []
                    : [
                          {
                              id: '',
                              rowType: 'refundFee',
                              name: t('orders.orders.cancelAndRefund.visitorRefundFee'),
                              description:
                                  partnerRefundFeeType === 'percentage'
                                      ? t('orders.orders.cancelAndRefund.feePercentage', { fee: partnerRefundFee })
                                      : t('orders.orders.cancelAndRefund.flatRate'),
                              amount: partnerRefundFeeAmount,
                              selected: partnerRefundFeeSelected,
                              orderItemId: '',
                              infoTip: t('orders.orders.cancelAndRefund.refundFeeInfo'),
                          },
                      ],
        }

        sections.push(refundFeeSection)
    }

    const totalAmountSection = {
        id: 'totalSection',
        rows: [
            {
                id: '',
                rowType: 'total',
                name: t('orders.orders.cancelAndRefund.total'),
                description: showPartialRedeemWarning ? t('orders.orders.cancelAndRefund.single.includesRedeemed') : '',
                amount: totalAmount,
                selected: false,
                orderItemId: '',
            },
        ],
    }
    sections.push(totalAmountSection)

    return {
        sections,
    }
}

interface PostCancellationTableDataMapperOptions {
    paidAmount: number
    totalAmount: number
    partnerRefundFeeAmount: number
    partnerRefundFeeOff: boolean
    partnerRefundFeeSelected: boolean
    partnerRefundFee?: string
    partnerRefundFeeType?: RefundFeeType | null
    confirmMode: boolean
}

// A cancelled order's tickets are already void, so there is nothing to
// select: the dialog shows the held payment, the optional visitor refund
// fee, and the total that will be refunded.
export const preparePostCancellationTableData = ({
    paidAmount,
    totalAmount,
    partnerRefundFeeAmount,
    partnerRefundFeeOff,
    partnerRefundFeeSelected,
    partnerRefundFee,
    partnerRefundFeeType,
    confirmMode,
}: PostCancellationTableDataMapperOptions): CancelRefundTableData => {
    const sections: CancelRefundTableSection[] = [
        {
            id: 'itemsSection',
            rows: [
                {
                    id: 'paidAmount',
                    rowType: 'total',
                    name: t('orders.orders.cancelAndRefund.single.amountPaid'),
                    description: t('orders.orders.cancelAndRefund.single.cancelledOrderVoid'),
                    amount: paidAmount,
                    selected: false,
                    orderItemId: '',
                },
            ],
            borderBottom: true,
        },
    ]

    if (!partnerRefundFeeOff) {
        sections.push({
            id: 'refundFeeSection',
            rows:
                confirmMode && !partnerRefundFeeSelected
                    ? []
                    : [
                          {
                              id: '',
                              rowType: 'refundFee',
                              name: t('orders.orders.cancelAndRefund.visitorRefundFee'),
                              description:
                                  partnerRefundFeeType === 'percentage'
                                      ? t('orders.orders.cancelAndRefund.feePercentage', { fee: partnerRefundFee })
                                      : t('orders.orders.cancelAndRefund.flatRate'),
                              amount: partnerRefundFeeAmount,
                              selected: partnerRefundFeeSelected,
                              orderItemId: '',
                              infoTip: t('orders.orders.cancelAndRefund.refundFeeInfo'),
                          },
                      ],
        })
    }

    sections.push({
        id: 'totalSection',
        rows: [
            {
                id: '',
                rowType: 'total',
                name: t('orders.orders.cancelAndRefund.total'),
                description: '',
                amount: totalAmount,
                selected: false,
                orderItemId: '',
            },
        ],
    })

    return { sections }
}

interface FooterPropsMapperOptions {
    accountSlug: string
    confirmMode: boolean
    isCancellation: boolean
    fullRefundOnly: boolean
    refunding: boolean
    onCancel: () => void
    onNext: () => void
    totalAmount: number
    noTicketsSelected: boolean
    refundDisallowed: boolean
    formatCurrencyString: (amount: number | string, accountSlug: string) => string
    customerEmail?: string
}

export const mapFooterData = ({
    accountSlug,
    confirmMode,
    isCancellation,
    fullRefundOnly,
    refunding,
    onCancel,
    onNext,
    totalAmount,
    noTicketsSelected,
    refundDisallowed,
    formatCurrencyString,
    customerEmail = '',
}: FooterPropsMapperOptions): CancelRefundFooterProps => {
    const confirmMessage = (() => {
        if (confirmMode) {
            return t('orders.orders.cancelAndRefund.single.doubleCheck')
        } else {
            if (fullRefundOnly) {
                return isCancellation
                    ? t('orders.orders.cancelAndRefund.single.onlyFullCancellation')
                    : t('orders.orders.cancelAndRefund.single.onlyFullRefund')
            } else {
                return isCancellation
                    ? t('orders.orders.cancelAndRefund.single.partialCancellationHint')
                    : t('orders.orders.cancelAndRefund.single.partialRefundHint')
            }
        }
    })()

    const confirmMessageImportant = (() => {
        if (confirmMode) {
            return isCancellation
                ? t('orders.orders.cancelAndRefund.single.cancellationsUndone')
                : t('orders.orders.cancelAndRefund.single.refundsUndone')
        } else {
            return ''
        }
    })()

    const email = (() => {
        if (!customerEmail) {
            return ''
        }

        if (customerEmail.length > 40) {
            return `${customerEmail.slice(0, 40)}...`
        } else {
            return customerEmail
        }
    })()

    const nextButtonCaption = confirmMode
        ? isCancellation
            ? t('orders.orders.cancelAndRefund.single.cancelItems')
            : email
            ? t('orders.orders.cancelAndRefund.single.refundAmountTo', {
                  amount: formatCurrencyString(Number(totalAmount).toFixed(2), accountSlug),
                  email,
              })
            : t('orders.orders.cancelAndRefund.single.refundAmount', {
                  amount: formatCurrencyString(Number(totalAmount).toFixed(2), accountSlug),
              })
        : t('orders.orders.cancelAndRefund.review')

    return {
        confirmMessage,
        confirmMessageImportant,
        cancelButtonCaption: confirmMode ? t('orders.orders.cancelAndRefund.backToEditing') : t('common.cancel'),
        onCancel,
        nextButtonCaption,
        onNext,
        nextButtonDisabled: refundDisallowed || noTicketsSelected || refunding,
    }
}
